import { Injectable, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { DropdownInput } from 'root/shared/components/form/dropdown/dropdown.interface';
import { UserFrontendStateKey, FilterType, FilterValuePair, MachineFilterResponseValue, UserFilterPreset, UserFilterPresetDto } from 'root/mibp-openapi-gen/models';
import { Subscription, firstValueFrom } from 'rxjs';
import { GlobalConfigService, LogService, MibpLogger } from '..';
import { MibpSessionService } from '../mibp-session/mibp-session.service';
import { MachineFilterSelectionNew } from 'root/components/machine-filter-new/machine-filter-selection-new.interface';
import { usersFnGetFilterPresets } from "root/mibp-openapi-gen/fn/users/users-fn-get-filter-presets";
import { usersFnSaveFilterPresets } from "root/mibp-openapi-gen/fn/users/users-fn-save-filter-presets";
import { MibpApiService } from "root/mibp-openapi-gen/mibp-api.service";

interface FilterPresetUpdate {
  timeoutId?: any;
  key: UserFrontendStateKey;
  userFilterPresetValues: UserFilterPreset[];
}

interface SupportedFilters {
  presetKey: UserFrontendStateKey;
  filterKeys?: FilterKey[];
}

interface FilterKey {
  key: string;
  type: 'date' | 'string';
}

@Injectable({
  providedIn: 'root'
})
export class FilterPresetService implements OnDestroy {
    private mibpApiService = inject(MibpApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private globalConfig = inject(GlobalConfigService);
  private mibpSession = inject(MibpSessionService);

  private readonly storageKeyPrefix = "mibp.filterpresetservice.";
  private filterPresets: UserFilterPresetDto[];
  private pendingUpdates: FilterPresetUpdate[] = [];
  private useDelayedUpdates = false;
  private updateDelay: 10000; //Milliseconds before changes are pushed to backend
  private fetchingFromBackendPromise: Promise<UserFilterPresetDto[]>;
  private isUserNavigating = false;
  private log: MibpLogger;
  private routerSubscription?: Subscription;
  private readonly sharedPunchoutUserEmail = "punchoutcxmluser@mibp.net";


  private readonly allMachineFilterKeys: { key: keyof MachineFilterSelectionNew }[] = [
    { key: "company" },
    { key: "customer" },
    { key: "secondaryCustomer" },
    { key: "operationSite" },
    { key: "machine" },
    { key: "model" },
    { key: "modelSerialAlias" }
  ];

  /**********************************************/
  // Filters using the filter={...} query parameter
  /**********************************************/
  private supportedCommonFilters = [
    {
      presetKey: UserFrontendStateKey.FilterKitFinder,
      filterKeys: [
        { key: "kitTypes" },
        { key: "intervalHours" },
        { key: "machineHours" },
        { key: "componentTypes" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterMachineKitFinder,
      filterKeys: [
        { key: "kitTypes" },
        { key: "intervalHours" },
        { key: "machineHours" },
        { key: "componentTypes" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterElectronicManuals,
      filterKeys: [
        { key: "category" },
        { key: "language" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterMyFleet,
      filterKeys: [
        { key: "status" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterFleetDocuments,
      filterKeys: [
        { key: "DocumentType" },
        { key: "AssetType" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterMachineOrders,
      filterKeys: [
        { key: "orderStatus" },
        { key: "from", type: "date" },
        { key: "to", type: "date" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterBulletins,
      filterKeys: [
        { key: "bulletinType" },
        { key: "from", type: "date" },
        { key: "to", type: "date" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterComponentFinderBooms,
      filterKeys: [
        { key: "machineModel" },
        { key: "modelName" },
        { key: "offeringType" },
        { key: "component" },
        { key: "pistonRod" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterComponentFinderFeeds,
      filterKeys: [
        { key: "machineModel" },
        { key: "modelName" },
        { key: "offeringType" },
        { key: "rockDrill" },
        { key: "feedLength" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterTemplates,
      filterKeys: [
        { key: "status" },
        { key: "from", type: "date" },
        { key: "to", type: "date" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterQuotations,
      filterKeys: [
        { key: "salesentity" },
        { key: "customer" },
        { key: "deliverysequence" },
        { key: "status" },
        { key: "from", type: "date" },
        { key: "to", type: "date" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterOrders,
      filterKeys: [
        { key: "salesentity" },
        { key: "customer" },
        { key: "deliverysequence" },
        { key: "status" },
        { key: "from", type: "date" },
        { key: "to", type: "date" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterPromotions,
      filterKeys: [
        { key: "company" }
      ]
    }
  ] as SupportedFilters[];

  /**********************************************/
  // Filters using the f={...} query parameter
  /**********************************************/
  private supportedCommonFFilters = [
    {
      presetKey: UserFrontendStateKey.FilterMaintenanceKitCalendar,
      filterKeys: [
        { key: "serialNumbers" },
        { key: "models" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterDownloadedManuals,
      filterKeys: [
        { key: "statuses" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterSearchProducts,
      filterKeys: [
        { key: "image" },
        { key: "type" },
        { key: "class" },
        { key: "equipment" },
        { key: "operationSite" }
      ]
    }
  ] as SupportedFilters[];

  /**********************************************/
  // Filters using the machinefilters={...} query parameter
  /**********************************************/
  private supportedMachineFilters = [
    {
      presetKey: UserFrontendStateKey.FilterSearchProducts,
      filterKeys: [
        { key: "machine" },
        { key: "operationSite" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterMaintenanceKitCalendar,
      filterKeys: this.allMachineFilterKeys
    },
    {
      presetKey: UserFrontendStateKey.FilterKitFinder,
      filterKeys: [
        { key: "modelSerialAlias" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterMachineKitFinder,
      filterKeys: [
        { key: "modelSerialAlias" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterElectronicManuals,
      filterKeys: this.allMachineFilterKeys
    },
    {
      presetKey: UserFrontendStateKey.FilterBulletins,
      filterKeys: this.allMachineFilterKeys
    },
    {
      presetKey: UserFrontendStateKey.FilterLocations,
      filterKeys: this.allMachineFilterKeys
    },
    {
      presetKey: UserFrontendStateKey.FilterMyFleet,
      filterKeys: [
        { key: "company" },
        { key: "customer" },
        { key: "secondaryCustomer" },
        { key: "operationSite" },
        { key: "model" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterDownloadedManuals,
      filterKeys: [
        { key: "company" },
        { key: "customer" },
        { key: "operationSite" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterFleetDocuments,
      filterKeys: [
        { key: "company" },
        { key: "customer" },
        { key: "operationSite" },
        { key: "model" },
        { key: "machine" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterWarrantyClaims,
      filterKeys: [
        { key: "company" },
        { key: "customer" },
        { key: "operationSite" },
        { key: "machine" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterWarrantyApplications,
      filterKeys: [
        { key: "company" },
        { key: "customer" },
        { key: "operationSite" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterWarrantyRegistrations,
      filterKeys: [
        { key: "company" },
        { key: "customer" },
        { key: "operationSite" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterOrders,
      filterKeys: [
        { key: "company" },
        { key: "customer" }
      ]
    },
    {
      presetKey: UserFrontendStateKey.FilterQuotations,
      filterKeys: [
        { key: "company" },
        { key: "customer" }
      ]
    }
  ] as SupportedFilters[];

  /**********************************************/
  // Filters using the auto generated grid query parameters
  /**********************************************/
  private supportedGridFilters = [
    { presetKey: UserFrontendStateKey.FilterSupportCases },
    { presetKey: UserFrontendStateKey.FilterWarrantyClaims },
    { presetKey: UserFrontendStateKey.FilterWarrantyApplications },
    { presetKey: UserFrontendStateKey.FilterWarrantyRegistrations }
  ] as SupportedFilters[];

  constructor() {
    const logService = inject(LogService);

    this.log = logService.withPrefix('filter-preset-service');

    this.routerSubscription = this.router.events.subscribe({
      next: event => {
        if (event instanceof NavigationEnd) {
          this.isUserNavigating = true;
        } else if (event instanceof NavigationStart) {
          const currentRoute = this.router.routerState;
          const currentRouteBaseUrl = currentRoute.snapshot?.url?.split('?')[0];
          const targetRouteBaseUrl = event.url?.split('?')[0];

          if (currentRouteBaseUrl == targetRouteBaseUrl) {
            const navigation = this.router.getCurrentNavigation();

            // Router links in sidebar.component.html has [info]="..." set on them that will be forwarded when
            // they are clicked. Those parameters are then found under navigation.extras.info.
            if (navigation.extras?.info && navigation.extras.info["triggeredFromNav"]) {
              //We want to keep existing query params so we pick them from current route
              this.router.navigate([currentRouteBaseUrl], { queryParams: currentRoute.snapshot?.root?.queryParams});
            }
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  public async loadFilterPresetsForCurrentUser() {
    try {
      if (!this.useFilterPresets()) {
        return;
      }

      this.fetchingFromBackendPromise = firstValueFrom(this.mibpApiService.invoke(usersFnGetFilterPresets));
      this.filterPresets = await this.fetchingFromBackendPromise;
    } catch (e) {
      this.log.error("Error when getting filter presets for user", e);
      this.filterPresets = [];
    } finally {
      this.fetchingFromBackendPromise = null;
    }
  }

  //TODO: Clean up this method. Could probably create some reusable private method for some operations
  public saveFilterChanges(key: UserFrontendStateKey, route: ActivatedRouteSnapshot = null) {
    try {
      if (!this.useFilterPresets()) {
        return;
      }

      const queryParams = route ? route.queryParams : this.route.snapshot.queryParams;

      const supportedCommonFilters = this.supportedCommonFilters.find(vf => vf.presetKey == key);
      const supportedCommonFFilters = this.supportedCommonFFilters.find(vf => vf.presetKey == key);
      const supportedMachineFilters = this.supportedMachineFilters.find(vf => vf.presetKey == key);
      const supportedGridFilters = this.supportedGridFilters.find(vf => vf.presetKey == key);

      let commonFilters = queryParams['filter'];
      let commonFFilters = queryParams['f'];
      let machineFilters = queryParams?.machinefilter;
      let commonFilterParams: Record<string, string | DropdownInput[]>;
      let commonFParams: Record<string, any>;
      let machineFilterParams: Record<string, MachineFilterResponseValue[]>;
      let gridFiltersPresets = [];
      let savedGridPresets = [];

      const savedFilterPresets = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.Common);
      const savedMachinefilterPresets = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.Machine);
      const savedFPresets = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.ValueOnly);

      const currentFilterPresetsKeyValueMap = this.toKeyValuePairMap(savedFilterPresets);
      const currentFFilterPresetsKeyValueMap = this.toKeyValuePairMap(savedFPresets, true);
      const currentMachineFilterPresetsKeyValueMap = this.toKeyValuePairMap(savedMachinefilterPresets);

      //#region Populate filter data

      if (supportedCommonFilters && commonFilters) {
        try {
          commonFilterParams = JSON.parse(commonFilters);

          Object.keys(commonFilterParams).forEach(paramFilterkey => {
            if (!this.supportedCommonFilters.find(vf => vf.presetKey == key).filterKeys.some(fk => fk.key == paramFilterkey)) {
              delete commonFilterParams[paramFilterkey];
            }
          });
        } catch {
          commonFilterParams = {};
        }
      } else {
        commonFilterParams = {};
        commonFilters = '{}';
      }

      if (supportedMachineFilters && machineFilters) {
        try {
          machineFilterParams = JSON.parse(machineFilters);

          Object.keys(machineFilterParams).forEach(paramFilterkey => {
            if (!this.supportedMachineFilters.find(vf => vf.presetKey == key).filterKeys.some(fk => fk.key == paramFilterkey)) {
              delete machineFilterParams[paramFilterkey];
            }
          });
        } catch {
          machineFilterParams = {};
        }
      } else {
        machineFilterParams = {};
        machineFilters = '{}';
      }

      if (supportedCommonFFilters && commonFFilters) {
        try {
          commonFParams = JSON.parse(commonFFilters);
          Object.keys(commonFParams).forEach(paramFilterkey => {
            if (!this.supportedCommonFFilters.find(vf => vf.presetKey == key).filterKeys.some(fk => fk.key == paramFilterkey)) {
              delete commonFParams[paramFilterkey];
            }
          });
        } catch {
          commonFParams = {};
        }
      } else {
        commonFParams = {};
        commonFFilters = '{}';
      }

      if (supportedGridFilters) {
        gridFiltersPresets = this.getGridFilterPresetsFromQueryString(queryParams);
        savedGridPresets = this.getFilterPresetByKey(key)
          .filter(fp => fp.type == FilterType.Grid)
          .sort((a, b) => a.name.localeCompare(b.name));
      }

      //#endregion

      const filtersChanged = JSON.stringify(currentFilterPresetsKeyValueMap) != commonFilters ||
        JSON.stringify(currentMachineFilterPresetsKeyValueMap) != machineFilters ||
        JSON.stringify(currentFFilterPresetsKeyValueMap) != commonFFilters ||
        JSON.stringify(savedGridPresets) != JSON.stringify(gridFiltersPresets);

      if (filtersChanged) {
        this.handleSave(key, commonFilterParams, commonFParams, machineFilterParams, gridFiltersPresets);
      }
    } catch (e) {
      this.log.error("An unhandled exception occured when trying to save user filter presets", e);
    }
  }

  public applyPresetFiltersQueryString(key: UserFrontendStateKey, route: ActivatedRouteSnapshot): void {
    // We try catch this whole function since this is called from resolvers and
    // an unhandled exception from this would break the whole application.
    try {
      if (!this.useFilterPresets(route)) {
        return;
      }

      let commonFilterParams: Record<string, any> = {};
      let commonfParams: Record<string, any> = {};
      let machineFilterParams: Record<string, any> = {};
      let gridFilterParams: Record<string, string> = {};

      // Common filters using filter query string and key value pairs
      const existingFilterQuery = route.queryParams['filter'];

      if (!existingFilterQuery) {
        const commonFilterPresets = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.Common);
        const filterKeys = this.supportedCommonFilters.find(sf => sf.presetKey == key)?.filterKeys;
        commonFilterParams = this.toKeyValuePairMap(commonFilterPresets, false, filterKeys ?? []);
      }

      // Common filters using f query string and keys only
      const existingFQuery = route.queryParams['f'];

      if (!existingFQuery) {
        const commonFPresets = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.ValueOnly);
        commonfParams = this.toKeyValuePairMap(commonFPresets, true);
      }

      // Machine filters using machinefilter query string and key value pairs
      const existingMachineFilterQuery = route.queryParams?.machinefilter;

      if (!existingMachineFilterQuery) {
        const machinefilterPresets = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.Machine);
        machineFilterParams = this.toKeyValuePairMap(machinefilterPresets);
      }

      // Filters used by MIBP grid component
      const gridFilterPrefix = this.getGridFilterPrefix(route.queryParams);

      if (!gridFilterPrefix) {
        gridFilterParams = this.getGridFilters(key, gridFilterPrefix);
      }

      if (!this.isUserNavigating) {
        this.saveFilterChanges(key, route);
      }

      // If we don´t have any filters we can just return
      if (Object.keys(commonFilterParams).length == 0 && Object.keys(machineFilterParams).length == 0 &&
        Object.keys(commonfParams).length == 0 && Object.keys(gridFilterParams).length == 0) {
        return;
      }

      const params =
      {
        filter: Object.keys(commonFilterParams).length > 0 ? JSON.stringify(commonFilterParams) : null,
        machinefilter: Object.keys(machineFilterParams).length > 0 ? JSON.stringify(machineFilterParams) : null,
        f: Object.keys(commonfParams).length > 0 ? JSON.stringify(commonfParams) : null,
        ...gridFilterParams
      };

      const currentPath = this.getResolvedUrl(route);
      this.router.navigate([currentPath], { queryParams: params, queryParamsHandling: this.isUserNavigating ? 'replace' : 'merge' });
      this.isUserNavigating = false;
    } catch (e) {
      this.log.error("An unhandled exception occured when trying to apply user filter presets", e);
    }
  }

  public clearFiltersByKey(key: UserFrontendStateKey) {
    if (!this.useFilterPresets()) {
      return;
    }

    this.saveFilterPresetByKey(key, []);
  }

  public clearAllFilters() {
    this.filterPresets = [];
  }

  private useFilterPresets(route: ActivatedRouteSnapshot = null) : boolean {
    // Filter presets should not be used if global flag is false OR if current user is the shared "global" Punchout account
    if (!this.globalConfig.enableFilterPresets || this.mibpSession.current?.user?.email == this.sharedPunchoutUserEmail || this.mibpSession.current?.user == null) {
      return false;
    }

    // Filter presets should not be used if navigated from header search for product and url having "query" parameter
    if(route?.data){
      const skipQueryParams = route.data['skipFilterPresetIfQueryParams'];
      if(skipQueryParams){
        let skipFilterPreset = false;
        skipQueryParams?.map(param => {
          const queryParamValue  = route.queryParamMap.get(param);
          skipFilterPreset = queryParamValue?.length > 0;
        });
        if(skipFilterPreset){
          return false;
        }
      }
    }
    return true;
  }

  private getGridFilters(key: UserFrontendStateKey, filterPrefix: string, forceLoad = false): Record<string, string> {
    const gridFilterParams: Record<string, string> = {};

    if (filterPrefix == "" && !forceLoad) {
      return gridFilterParams;
    }

    const gridFilters = this.getFilterPresetByKey(key).filter(fp => fp.type == FilterType.Grid);

    gridFilters.forEach(gf => {
      const values = (gf.values || []).map(v => v.text.concat("|", v.value));
      gridFilterParams[gf.name] = JSON.stringify(values);
    });

    return gridFilterParams;
  }

  private getGridFilterPresetsFromQueryString(queryParams: Params) {
    const filterPrefix = this.getGridFilterPrefix(queryParams);

    if (!filterPrefix) {
      return [];
    }

    return Object.keys(queryParams).filter(p => p.startsWith(filterPrefix)).map(fp => {
      return {
        name: fp,
        type: FilterType.Grid,
        values: this.createValuePairList(queryParams[fp])
      } as UserFilterPreset;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }

  private getGridFilterPrefix(queryParams): string {
    const prefix = Object.keys(queryParams).find(p => p.match(/^[a-z]+\.\$/i));
    return prefix ? prefix.substring(0, prefix.indexOf('$') + 1) : null;
  }

  private createValuePairList(rawParamValue: unknown): FilterValuePair[] {
    let paramValues: string[];
    try {
      paramValues = JSON.parse(rawParamValue as string) as string[];
    } catch {
      this.log.error("Could not parse grid filter query parameter value. Returning empty array", { rawParamValue });
      paramValues = [];
    }

    if (paramValues && Array.isArray(paramValues)) {
      return paramValues.map(pv => {
        return { text: pv.split("|")[0], value: pv.split("|")[1] };
      });
    }

    return [];
  }

  private handleSave(key: UserFrontendStateKey, commonFilterParams: Record<string, any>,
    commonFFilterParams: Record<string, any>, machineFilterParams: Record<string, any>, gridFilterPresets: UserFilterPreset[]) {

    const commonPresets = Object.keys(commonFilterParams).map(fp => {
      return {
        name: fp,
        type: FilterType.Common,
        values: Array.isArray(commonFilterParams[fp]) ? commonFilterParams[fp] : [{ value: commonFilterParams[fp] }]
      } as UserFilterPreset;
    });

    const commonFPresets = Object.keys(commonFFilterParams)
      .filter(fp => Array.isArray(commonFFilterParams[fp]))
      .map(fp => {
        return {
          name: fp,
          type: FilterType.ValueOnly,
          values: commonFFilterParams[fp].map(cf => {
            return { value: cf, text: cf } as FilterValuePair;
          })
        } as UserFilterPreset;
      });

    const machinePresets = Object.keys(machineFilterParams).map(fp => {
      return { name: fp, type: FilterType.Machine, values: machineFilterParams[fp] } as UserFilterPreset;
    });

    const allFilterPresets = commonPresets.concat(commonFPresets, machinePresets, gridFilterPresets);

    this.saveFilterPresetByKey(key, allFilterPresets);
  }

  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(v => v.url.map(segment => segment.toString()).join('/'))
      .join('/');
  }

  private toKeyValuePairMap(userFilterPresets: UserFilterPreset[], useKeyOnly = false, supportedFilters: FilterKey[] = []) {
    const querystringFilter: Record<string, any> = {};

    userFilterPresets.forEach(mf => {
      querystringFilter[mf.name] = supportedFilters.find(sf => sf.key == mf.name)?.type == 'date' ? mf.values[0].value :
        useKeyOnly ? mf.values.map(v => v.value) : mf.values;
    });

    return querystringFilter;
  }

  private getFilterPresetByKey(key: UserFrontendStateKey): UserFilterPreset[] {
    const foundPreset = this.filterPresets?.find(fp => fp.userFrontendStateKey == key && fp.userFilterPresetValues);
    return foundPreset ? foundPreset.userFilterPresetValues ? foundPreset.userFilterPresetValues : [] : [];

    // //If using local storage
    // const filterPresetAsJson = localStorage.getItem(this.storageKeyPrefix.concat(key.toString()));
    // const filterPresets = filterPresetAsJson ? JSON.parse(filterPresetAsJson) as UserFilterPreset[] : [];
    // return filterPresets;
  }

  private saveFilterPresetByKey(key: UserFrontendStateKey, userFilterPresetValues: UserFilterPreset[]) {
    let filterPresetToSave = this.filterPresets.find(fp => fp.userFrontendStateKey == key);

    if (!filterPresetToSave) {
      filterPresetToSave = { userFrontendStateKey: key, userFilterPresetValues: userFilterPresetValues } as UserFilterPresetDto;
      this.filterPresets.push(filterPresetToSave);
    } else {
      filterPresetToSave.userFilterPresetValues = userFilterPresetValues;
    }

    if (this.useDelayedUpdates) {
      this.queueUpdate(key, filterPresetToSave.userFilterPresetValues);
    } else {
      firstValueFrom(this.mibpApiService.invoke(usersFnSaveFilterPresets, {
                  body: {
                    userFrontendStateKey: key,
                    filterPresets: userFilterPresetValues
                  }
                })).catch((e) => {
        this.log.error("Error when saving filters", e);
      });
    }
  }

  private queueUpdate(key: UserFrontendStateKey, userFilterPresetValues: UserFilterPreset[]) {
    // Check if we have key in queue already
    let pendingUpdate = this.pendingUpdates.find(pu => pu.key == key);

    // If an update is already queued -> Clear timeout and remove update from queue
    if (pendingUpdate) {
      clearTimeout(pendingUpdate.timeoutId);

      // In case callback ran just before clearTimeout we make sure that pending update is still here
      const index = this.pendingUpdates.findIndex(pendingUpdate => pendingUpdate.key == key);

      if (index != -1) {
        this.pendingUpdates.splice(index, 1);
      }
    }

    pendingUpdate = {
      key: key,
      userFilterPresetValues: userFilterPresetValues
    } as FilterPresetUpdate;

    this.pendingUpdates.push(pendingUpdate);

    const timeoutId = setTimeout(() => this.pushChangesToBackend, this.updateDelay, this.pendingUpdates);
    pendingUpdate.key = timeoutId;
  }

  private async pushChangesToBackend(pendingUpdates: FilterPresetUpdate[]) {
    if (pendingUpdates && pendingUpdates.length > 0) {
      const filterPresetToUpdate = pendingUpdates.shift();

      await firstValueFrom(this.mibpApiService.invoke(usersFnSaveFilterPresets, {
                  body: {
                    userFrontendStateKey: filterPresetToUpdate.key,
                    filterPresets: filterPresetToUpdate.userFilterPresetValues
                  }
                }));
    }
  }
}
